import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePortalAuth } from '../PortalAuthContext';
import { PortalInlineAlert } from '../ui/PortalInlineAlert';

export const PortalMagicLinkForm = ({ email, setEmail, loginPath }) => {
  const { requestMagicLink } = usePortalAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      await requestMagicLink(email, loginPath);
      setSent(true);
      toast.success('If an account exists for that email, a sign-in link is on its way.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not send the sign-in link');
    } finally {
      setBusy(false);
    }
  };

  if (sent) return (
    <div className="space-y-3" data-testid="portal-magic-sent">
      <PortalInlineAlert status="success" testId="portal-magic-sent-banner">
        <span className="inline-flex items-start gap-1.5">
          <MailCheck className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          If an account exists for {email}, a sign-in link is on its way. It works once and expires in 15 minutes.
        </span>
      </PortalInlineAlert>
      <button type="button" className="w-full text-xs text-center text-slate-500 hover:underline"
        data-testid="portal-magic-resend" onClick={send} disabled={busy}>Didn't get it? Send again</button>
    </div>
  );

  return (
    <form onSubmit={send} className="space-y-3" data-testid="portal-magic-form">
      {error && <PortalInlineAlert status="danger" testId="portal-magic-error">{error}</PortalInlineAlert>}
      <div className="space-y-2">
        <Label htmlFor="portal-magic-email">Email</Label>
        <Input id="portal-magic-email" data-testid="portal-magic-email-input" type="email" autoComplete="email"
          value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} required placeholder="you@example.com" />
      </div>
      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-magic-submit" disabled={busy || !email}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Email me a sign-in link'}
      </Button>
    </form>
  );
};
